import {
  BarChart3,
  Settings,
  Users,
  Mail,
  Activity,
  Bot,
  FileText,
  Bell,
  FileSpreadsheet,
  Shield,
  Home,
  Wallet,
  Gift,
  Network,
  Handshake,
  Folder,
  Cog,
  Building,
  LayoutGrid,
  ShoppingCart,
  CalendarDays,
  Package,
  FileCheck,
  CreditCard,
  History,
  TrendingUp,
  Briefcase,
  HeadphonesIcon,
  Wrench,
  PenTool,
  Megaphone,
  Server,
  Receipt,
  DollarSign,
  FileSignature,
  Target,
  TicketIcon,
  BookOpen,
  Clock,
  StickyNote,
  MessageSquare,
  PieChart,
  Tag,
  MailOpen,
  Megaphone as Announcement,
  UserCheck,
  Star,
  FileArchive,
  Layers,
  QrCode,
  UtensilsCrossed,
  MapPin,
  Boxes,
  Sparkles,
  Phone,
  Globe,
  type LucideIcon,
} from 'lucide-react';

export interface MenuItem {
  path: string;
  icon: LucideIcon;
  label: string;
  basicUser?: boolean;
  adminOnly?: boolean;
  allowedRoles?: string[];
}

export interface MenuCategory {
  label: string;
  icon: LucideIcon;
  color: string;        // Tailwind text color class
  bgColor: string;      // Tailwind bg color class (light)
  adminOnly?: boolean;
  allowedRoles?: string[];
  sectionGroup?: string; // Groups categories under a section header
  items: MenuItem[];
}

export type MenuCategories = Record<string, MenuCategory>;

export const menuCategories: MenuCategories = {

  // ─── USER ──────────────────────────────────────────────────────────────────

  dashboard: {
    label: 'Dashboard',
    icon: Home,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    items: [
      { path: '/dashboard', icon: Home, label: 'Overview', basicUser: true },
      { path: '/dashboard/notifications', icon: Bell, label: 'Notifications', basicUser: true },
    ]
  },

  myAccount: {
    label: 'My Account',
    icon: Users,
    color: 'text-violet-500',
    bgColor: 'bg-violet-500/10',
    items: [
      { path: '/dashboard/wallet', icon: Wallet, label: 'Wallet', basicUser: true },
      { path: '/dashboard/rewards', icon: Star, label: 'Rewards', basicUser: true },
      { path: '/dashboard/invoices', icon: FileSpreadsheet, label: 'My Invoices', basicUser: true },
      { path: '/dashboard/payment-methods', icon: CreditCard, label: 'Payment Methods', basicUser: true },
      { path: '/dashboard/settings', icon: Settings, label: 'Settings', basicUser: true },
    ]
  },

  partner: {
    label: 'Partner Program',
    icon: Handshake,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    items: [
      { path: '/dashboard/join', icon: Handshake, label: 'Join Program', basicUser: true },
      { path: '/dashboard/affiliate', icon: Users, label: 'Affiliate Portal', basicUser: true },
      { path: '/dashboard/partner-portal', icon: Network, label: 'MLM Portal', basicUser: true },
      { path: '/marketer', icon: Megaphone, label: 'Marketer Dashboard', basicUser: true },
    ]
  },

  // ─── CRM ───────────────────────────────────────────────────────────────────

  customers: {
    label: 'Customers',
    icon: Users,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    sectionGroup: 'CRM',
    adminOnly: true,
    items: [
      { path: '/dashboard/clients', icon: Users, label: 'Clients', adminOnly: true },
      { path: '/dashboard/contacts', icon: UserCheck, label: 'Contacts', adminOnly: true },
    ]
  },

  leads: {
    label: 'Leads',
    icon: Target,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    sectionGroup: 'CRM',
    adminOnly: false,
    allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'],
    items: [
      { path: '/dashboard/leads', icon: Target, label: 'Leads', allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/sales-funnel', icon: TrendingUp, label: 'Sales Funnel', allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'] },
    ]
  },

  sales: {
    label: 'Sales',
    icon: DollarSign,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    sectionGroup: 'CRM',
    adminOnly: false,
    allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'],
    items: [
      { path: '/dashboard/estimates', icon: FileText, label: 'Estimates', allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/proposals', icon: FileSignature, label: 'Proposals', allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/proposal-generator', icon: Sparkles, label: 'Proposal Generator', allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/admin-invoices', icon: Receipt, label: 'Invoices', adminOnly: true },
      { path: '/dashboard/admin-payments', icon: CreditCard, label: 'Payments', adminOnly: true },
      { path: '/dashboard/credit-notes', icon: FileText, label: 'Credit Notes', adminOnly: true },
      { path: '/dashboard/coupons', icon: Tag, label: 'Coupons', allowedRoles: ['admin', 'owner', 'sales_marketing', 'marketer'] },
    ]
  },

  subscriptions: {
    label: 'Subscriptions',
    icon: TrendingUp,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    sectionGroup: 'CRM',
    adminOnly: true,
    items: [
      { path: '/dashboard/subscriptions', icon: TrendingUp, label: 'Subscriptions', adminOnly: true },
    ]
  },

  contracts: {
    label: 'Contracts',
    icon: FileArchive,
    color: 'text-slate-500',
    bgColor: 'bg-slate-500/10',
    sectionGroup: 'CRM',
    adminOnly: true,
    items: [
      { path: '/dashboard/admin-contracts', icon: FileArchive, label: 'Contracts', adminOnly: true },
    ]
  },

  // ─── PROJECTS & SUPPORT ────────────────────────────────────────────────────

  projects: {
    label: 'Projects',
    icon: Briefcase,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-500/10',
    sectionGroup: 'Work',
    adminOnly: true,
    items: [
      { path: '/dashboard/crm-projects', icon: Briefcase, label: 'Projects', adminOnly: true },
      { path: '/dashboard/tasks', icon: FileCheck, label: 'Tasks', adminOnly: true },
      { path: '/dashboard/tasks-kanban', icon: LayoutGrid, label: 'Kanban', adminOnly: true },
      { path: '/dashboard/time-tracking', icon: Clock, label: 'Time Tracking', adminOnly: true },
      { path: '/dashboard/milestones', icon: Target, label: 'Milestones', adminOnly: true },
    ]
  },

  support: {
    label: 'Support',
    icon: HeadphonesIcon,
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/10',
    sectionGroup: 'Work',
    adminOnly: true,
    items: [
      { path: '/dashboard/support-tickets', icon: TicketIcon, label: 'Tickets', adminOnly: true },
      { path: '/dashboard/knowledge-base', icon: BookOpen, label: 'Knowledge Base', adminOnly: true },
    ]
  },

  // ─── FINANCE ───────────────────────────────────────────────────────────────

  expenses: {
    label: 'Expenses',
    icon: Wallet,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
    sectionGroup: 'Finance',
    adminOnly: true,
    items: [
      { path: '/dashboard/expenses', icon: Wallet, label: 'Expenses', adminOnly: true },
    ]
  },

  // ─── HR ────────────────────────────────────────────────────────────────────

  hrm: {
    label: 'HRM',
    icon: Users,
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
    sectionGroup: 'HR',
    adminOnly: true,
    items: [
      { path: '/dashboard/hr', icon: Users, label: 'HR Dashboard', adminOnly: true },
      { path: '/dashboard/staff', icon: Users, label: 'Staff', adminOnly: true },
      { path: '/dashboard/departments', icon: Building, label: 'Departments', adminOnly: true },
      { path: '/dashboard/leave-management', icon: CalendarDays, label: 'Leave Management', adminOnly: true },
      { path: '/dashboard/performance-reviews', icon: Star, label: 'Performance', adminOnly: true },
      { path: '/dashboard/attendance', icon: Clock, label: 'Attendance', adminOnly: true },
    ]
  },

  // ─── MARKETING ─────────────────────────────────────────────────────────────

  marketing: {
    label: 'Marketing',
    icon: Megaphone,
    color: 'text-fuchsia-500',
    bgColor: 'bg-fuchsia-500/10',
    sectionGroup: 'Growth',
    adminOnly: false,
    allowedRoles: ['admin', 'owner', 'sales_marketing', 'marketer'],
    items: [
      { path: '/dashboard/marketing', icon: Mail, label: 'Campaigns', allowedRoles: ['admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/google-marketing', icon: Globe, label: 'Google Marketing', allowedRoles: ['admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/email-templates', icon: MailOpen, label: 'Email Templates', allowedRoles: ['admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/mlm-programs', icon: Network, label: 'MLM Programs', allowedRoles: ['admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/session-replays', icon: Activity, label: 'Session Replays', adminOnly: true },
    ]
  },

  // ─── ANALYTICS ─────────────────────────────────────────────────────────────

  analytics: {
    label: 'Analytics',
    icon: BarChart3,
    color: 'text-teal-500',
    bgColor: 'bg-teal-500/10',
    sectionGroup: 'Growth',
    adminOnly: true,
    items: [
      { path: '/dashboard/analytics', icon: BarChart3, label: 'Overview', adminOnly: true },
      { path: '/dashboard/audit-history', icon: History, label: 'Activity Log', adminOnly: true },
      { path: '/dashboard/analysis-history', icon: TrendingUp, label: 'Analysis History', adminOnly: true },
    ]
  },

  reports: {
    label: 'Reports',
    icon: PieChart,
    color: 'text-lime-500',
    bgColor: 'bg-lime-500/10',
    sectionGroup: 'Growth',
    adminOnly: true,
    items: [
      { path: '/dashboard/reports', icon: PieChart, label: 'Reports', adminOnly: true },
    ]
  },

  // ─── BUSINESS ──────────────────────────────────────────────────────────────

  ecommerce: {
    label: 'E-commerce',
    icon: ShoppingCart,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    sectionGroup: 'Business',
    adminOnly: true,
    items: [
      { path: '/dashboard/ecommerce', icon: ShoppingCart, label: 'Dashboard', adminOnly: true },
      { path: '/admin-products', icon: Package, label: 'Products', adminOnly: true },
      { path: '/admin-orders', icon: Receipt, label: 'Orders', adminOnly: true },
      { path: '/admin-categories', icon: Layers, label: 'Categories', adminOnly: true },
    ]
  },

  booking: {
    label: 'Booking',
    icon: CalendarDays,
    color: 'text-sky-500',
    bgColor: 'bg-sky-500/10',
    sectionGroup: 'Business',
    adminOnly: true,
    items: [
      { path: '/dashboard/booking', icon: CalendarDays, label: 'Booking System', adminOnly: true },
      { path: '/dashboard/admin-appointments', icon: CalendarDays, label: 'Appointments', adminOnly: true },
      { path: '/dashboard/admin-reservations', icon: MapPin, label: 'Reservations', adminOnly: true },
      { path: '/admin/restaurants', icon: Building, label: 'Restaurant Management', adminOnly: true },
      { path: '/dashboard/admin-floorplan', icon: LayoutGrid, label: 'Floor Plan', adminOnly: true },
      { path: '/dashboard/admin-services', icon: Package, label: 'Services', adminOnly: true },
    ]
  },

  giftCards: {
    label: 'Gift Cards',
    icon: Gift,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    sectionGroup: 'Business',
    adminOnly: true,
    items: [
      { path: '/dashboard/gift-cards', icon: Gift, label: 'Gift Cards', adminOnly: true },
    ]
  },

  // ─── CONTENT ───────────────────────────────────────────────────────────────

  content: {
    label: 'Content Tools',
    icon: LayoutGrid,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    sectionGroup: 'Content',
    adminOnly: true,
    items: [
      { path: '/dashboard/qr-codes', icon: QrCode, label: 'QR Codes', adminOnly: true },
      { path: '/dashboard/qr-codes/create', icon: QrCode, label: 'Create QR', adminOnly: true },
      { path: '/dashboard/my-pages', icon: LayoutGrid, label: 'My Pages', adminOnly: true },
      { path: '/dashboard/menu-builder', icon: UtensilsCrossed, label: 'Menu Builder', adminOnly: true },
      { path: '/dashboard/form-builder', icon: FileText, label: 'Form Builder', adminOnly: true },
      { path: '/dashboard/forms', icon: FileCheck, label: 'Forms', adminOnly: true },
      { path: '/dashboard/files', icon: Folder, label: 'File Manager', adminOnly: true },
    ]
  },

  contentManagement: {
    label: 'Publishing',
    icon: PenTool,
    color: 'text-violet-500',
    bgColor: 'bg-violet-500/10',
    sectionGroup: 'Content',
    adminOnly: true,
    items: [
      { path: '/dashboard/portfolio', icon: Briefcase, label: 'Portfolio', adminOnly: true },
      { path: '/dashboard/blog', icon: FileText, label: 'Blog', adminOnly: true },
      { path: '/dashboard/blog/content-strategy', icon: Target, label: 'Content Strategy', adminOnly: true },
      { path: '/dashboard/blog-categories', icon: Layers, label: 'Blog Categories', adminOnly: true },
      { path: '/dashboard/wordpress-import', icon: FileText, label: 'WordPress Import', adminOnly: true },
      { path: '/admin-newsletter', icon: Mail, label: 'Newsletter', adminOnly: true },
    ]
  },

  // ─── COMMUNICATION ─────────────────────────────────────────────────────────

  utilities: {
    label: 'Communication',
    icon: MessageSquare,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10',
    sectionGroup: 'Communication',
    adminOnly: true,
    items: [
      { path: '/dashboard/email-client', icon: Mail, label: 'Email Client', adminOnly: true },
      { path: '/dashboard/messages', icon: MessageSquare, label: 'Messages', adminOnly: true },
      { path: '/dashboard/announcements', icon: Announcement, label: 'Announcements', adminOnly: true },
      { path: '/dashboard/events', icon: CalendarDays, label: 'Calendar', adminOnly: true },
      { path: '/dashboard/notes', icon: StickyNote, label: 'Notes', adminOnly: true },
      { path: '/dashboard/reminders', icon: Bell, label: 'Reminders', adminOnly: true },
    ]
  },

  // ─── AI ────────────────────────────────────────────────────────────────────

  aiPhone: {
    label: 'AI Phone Agent',
    icon: Phone,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    sectionGroup: 'AI & SaaS',
    adminOnly: true,
    items: [
      { path: '/dashboard/ai-phone-command-center', icon: Phone, label: 'Command Center', adminOnly: true },
      { path: '/dashboard/ai-phone-agent', icon: Bot, label: 'Agent Setup', adminOnly: true },
    ]
  },

  saas: {
    label: 'SaaS Platform',
    icon: Boxes,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    sectionGroup: 'AI & SaaS',
    adminOnly: true,
    items: [
      { path: '/dashboard/saas-features', icon: Boxes, label: 'Features Dashboard', adminOnly: true },
      { path: '/dashboard/subscription-plans', icon: CreditCard, label: 'Subscription Plans', adminOnly: true },
      { path: '/dashboard/ai-editors', icon: Bot, label: 'AI Editors', allowedRoles: ['staff', 'admin', 'owner', 'sales_marketing', 'marketer'] },
      { path: '/dashboard/user-activity', icon: Activity, label: 'User Activity', adminOnly: true },
    ]
  },

  // ─── SETUP ─────────────────────────────────────────────────────────────────

  setup: {
    label: 'Setup',
    icon: Cog,
    color: 'text-gray-500',
    bgColor: 'bg-gray-500/10',
    sectionGroup: 'System',
    adminOnly: true,
    items: [
      { path: '/dashboard/user-management', icon: Users, label: 'Users', adminOnly: true },
      { path: '/dashboard/roles', icon: Shield, label: 'Roles & Permissions', adminOnly: true },
      { path: '/dashboard/security', icon: Shield, label: 'Security', adminOnly: true },
      { path: '/dashboard/site-config', icon: Cog, label: 'Site Config', adminOnly: true },
      { path: '/dashboard/page-seo', icon: Globe, label: 'Pages SEO', adminOnly: true },
      { path: '/dashboard/tenants', icon: Building, label: 'Tenants', adminOnly: true },
      { path: '/dashboard/api-gateway', icon: Server, label: 'API Gateway', adminOnly: true },
      { path: '/dashboard/api-settings', icon: Cog, label: 'API Settings', adminOnly: true },
      { path: '/dashboard/admin', icon: Shield, label: 'Admin Panel', adminOnly: true },
    ]
  },
};

// User categories (visible to all users)
export const userCategoryKeys = ['dashboard', 'myAccount', 'partner'];

// Admin categories (visible only to admin/owner)
export const adminCategoryKeys = Object.keys(menuCategories).filter(k => !userCategoryKeys.includes(k));

// Section group order for display
export const adminSectionGroups = ['CRM', 'Work', 'Finance', 'HR', 'Growth', 'Business', 'Content', 'Communication', 'AI & SaaS', 'System'];
